import axios from 'utils/axios';
import type {
    ApiKeyActionResult,
    ApiKeySavePayloadType,
    ApiKeyTypeRow,
    SourceLeadActionResult,
    SourceLeadSavePayloadType,
    SourceLeadType,
} from '@econtractor/types';

const handleDirApiError = <T extends { stat: boolean; errCode?: string }>(e: unknown): T | null => {
    if (e && typeof e === 'object' && ('stat' in e || 'errCode' in e)) return e as T;
    if (e && typeof e === 'object' && 'response' in e) {
        const response = (e as { response?: { data?: T } }).response;
        if (response?.data) return response.data;
    }
    console.error(e);
    return null;
};

export const getSourceLeads = async (): Promise<SourceLeadType[] | null> => {
    try {
        const response = await axios.get('/settings/source-leads');
        return response.data || [];
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const createSourceLead = async (payload: SourceLeadSavePayloadType): Promise<SourceLeadActionResult | null> => {
    try {
        const response = await axios.post('/settings/source-leads/create', payload);
        return response.data;
    } catch (e) {
        return handleDirApiError<SourceLeadActionResult>(e);
    }
};

export const updateSourceLead = async (
    sourceLeadId: number,
    payload: SourceLeadSavePayloadType,
): Promise<SourceLeadActionResult | null> => {
    try {
        const response = await axios.post(`/settings/source-leads/update/${sourceLeadId}`, payload);
        return response.data;
    } catch (e) {
        return handleDirApiError<SourceLeadActionResult>(e);
    }
};

export const deleteSourceLead = async (sourceLeadId: number): Promise<SourceLeadActionResult | null> => {
    try {
        const response = await axios.post(`/settings/source-leads/delete/${sourceLeadId}`);
        return response.data;
    } catch (e) {
        return handleDirApiError<SourceLeadActionResult>(e);
    }
};

export const getApiKeys = async (): Promise<ApiKeyTypeRow[] | null> => {
    try {
        const response = await axios.get('/settings/api-keys');
        return response.data || [];
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const getApiDocs = async (): Promise<Record<string, unknown> | null> => {
    try {
        const response = await axios.get('/settings/api-docs');
        return response.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const createApiKey = async (payload: ApiKeySavePayloadType): Promise<ApiKeyActionResult | null> => {
    try {
        const response = await axios.post('/settings/api-keys/create', payload);
        return response.data;
    } catch (e) {
        return handleDirApiError<ApiKeyActionResult>(e);
    }
};

export const updateApiKey = async (
    apiKeyId: number,
    payload: ApiKeySavePayloadType,
): Promise<ApiKeyActionResult | null> => {
    try {
        const response = await axios.post(`/settings/api-keys/update/${apiKeyId}`, payload);
        return response.data;
    } catch (e) {
        return handleDirApiError<ApiKeyActionResult>(e);
    }
};

export const deleteApiKey = async (apiKeyId: number): Promise<ApiKeyActionResult | null> => {
    try {
        const response = await axios.post(`/settings/api-keys/delete/${apiKeyId}`);
        return response.data;
    } catch (e) {
        return handleDirApiError<ApiKeyActionResult>(e);
    }
};
